import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { CookieService } from 'ngx-cookie';
import { takeUntil, Subject } from 'rxjs';
import { AppComponent } from 'src/app/app.component';
import { InfoReportGuard } from 'src/app/info-reports/info-report.guard';
import { InfoReportService } from 'src/app/services/info-report.service';
import { OktaAuthService } from 'src/app/services/okta.service';
import { LoginService } from 'src/app/services/login.service';
import { TokenService } from 'src/app/services/token.service';
import { environment } from 'src/environments/environment';
import { LoaderService } from 'src/app/services/loader.service';

enum ResponseMessageEnum {
  AccountDisabled = 'ness_account_disabled',
  AccountInactive = 'ness_account_disabled_due_to_inactive',
  AccountLocked = 'ness_account_locked_out',
  MFACodeInvalid = 'invalid_code',
  NoAppAccount = 'invalid_ness_user',
  NoOktaAccount = 'invalid_oklta_user',
  NotAuthenticated = 'invalid_grant',
  RequireAuthCode = 'MFA_REQUIRED',//'require_authentication_code',
  RequirePasswordReset = 'ChangePassword',
  LDAPAccountLocked = 'AccountLocked',
  LDAPAccountDisabled = 'AccountDisabled',
  LDAPLoginError = 'PasswordError',
  PasswordExpired = 'Password expired',
  OktaPasswordExpired = 'PASSWORD_EXPIRED',
  OktaAccountLockedOut = 'LOCKED_OUT',
  OktaPasswordReset = 'PASSWORD_RESET',
  RegistrationRequired = 'registration_required',
  Registred = 'registered',
  AccountDeleted = 'deleted'
}

@Component({
  selector: 'ness-doj-login-form',
  templateUrl: './doj-login-form.component.html',
  styleUrl: './doj-login-form.component.scss'
})
export class DojLoginFormComponent implements OnInit, OnDestroy {
  alertIconStyle: string = 'fas fa-info-circle';
  alertMessage: string = '';
  alertStyle: string = 'alert alert-info';
  adminsList: any[] = [];
  isAuthenticated: boolean = false;
  isLoginError: boolean = false;
  redirectUrl: string;
  dojloginUri: string = environment.oktaBaseUrl + environment.oktaLoginUrl;


  private _unsubscribe$: Subject<void> = new Subject<void>();

  constructor(private loginService: LoginService,
    private cookieService: CookieService,
    private route: ActivatedRoute,
    private router: Router,
    private infoReportGuard: InfoReportGuard,
    private infoReportService: InfoReportService,
    protected http: HttpClient,
    private tokenService: TokenService,
    private idle: AppComponent,
    private loaderService: LoaderService,
    private oktaAuth: OktaAuthService
  ) { }

  ngOnInit() {
    this.adminsList = [];
    let redirectUrl = sessionStorage.getItem('redirectUrl');
    if (redirectUrl != null) {
      this.redirectUrl = redirectUrl;
    }

    let loggedout = sessionStorage.getItem('logout');

    if (loggedout != 'true') {         
      this.findOktaSession();

      let accessToken = this.cookieService.get(environment.authCookieStorageKey);
      if (accessToken != null) {
        this.internalUserLogin(accessToken);
        this.validateUserToken();
      } else {
        this.route.queryParams.pipe(takeUntil(this._unsubscribe$)).subscribe((params: Params) => {
          let status = params['s'];
          let redirectTo = params['redirectUrl'];

          // display error message
          if (status != null && status == "1") {
            let loginErrorCookieValue: string = this.cookieService.get('login_error');
            if (loginErrorCookieValue && !!loginErrorCookieValue.length) {
              let loginErrorCookie = loginErrorCookieValue.split("&");
              let loginError = loginErrorCookie[1].slice(loginErrorCookie[1].indexOf("=") + 1);
              let admins = JSON.parse(loginErrorCookie[0].slice(loginErrorCookie[0].indexOf("=") + 1));
              if (loginError === ResponseMessageEnum.AccountDisabled || loginError === ResponseMessageEnum.AccountInactive) {
                this.alertStyle = 'alert alert-warning';
                this.alertIconStyle = 'fas fa-exclamation-circle';
                this.adminsList = [...admins];
                this.displayAlert("Your NESS account is currently disabled. Please contact your NESS Application Administrator for assistance with enabling your account.");
              }
            }
            this.cookieService.remove('login_error', { domain: environment.authCookieDomainKey });
          }

          if (redirectTo != null) {
            sessionStorage.setItem('redirectUrl', redirectTo);
          }

          this.validateUserToken();
        });
      }      
    }
    else {
      // Remove logout flag from sesstion storeage which prevent user
      // from automatically logged back in to NESS after logged out
      sessionStorage.removeItem('logout');
    }
    
    this.cookieService.remove('login_error', { domain: environment.authCookieDomainKey });
  }

  public ngOnDestroy() {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  public async findOktaSession() {

    if (this.oktaAuth.isFullyAuthenticated()) {
      // user is fully authenticated with OKTA (we have OKTA session AND OKTA access token), proceed with NESS PIV login
      this.loginService.login(this.oktaAuth.getOktaSessionFromStorage()).pipe(takeUntil(this._unsubscribe$)).subscribe({
        next: (auth: any) => {
          this.oktaAuth.clearStorage();
          if (auth != null) {
            this.loginSuccess(auth);
          }
        },
        error: (err: HttpErrorResponse) => {
          this.oktaAuth.clearStorage();
          this.loginFailed(err);
        }
      });
    } else {
      // Subscribe to OKTA user session events
      this.oktaAuth.$userSession.subscribe(userSession => {
        if (userSession) { // Make sure value is not null
          // We have an OKTA session, check to see if we have access token yet
          if (!this.oktaAuth.isFullyAuthenticated()) {
            // Force loaderService to activate until the login call redirects
            this.loaderService.status$.next(true);
            this.loaderService.status$.complete();
            // OKTA Session but no access token, use the okta Auth library login to get the access token
            // This will make another OKTA call with another NESS callback which reloads this page
            this.oktaAuth.login("/");
          }
        }
      });

      let cachedOktaSession = this.oktaAuth.getCachedOktaAccessTokenFromStorage();
      if (!cachedOktaSession) {
        // We don't have a cached OKTA session, call /me to look for one
        this.loginService.getOktaCurrentUserSession().pipe(takeUntil(this._unsubscribe$)).subscribe({
          next: (userSession: any) => {
            if (userSession) {
              let sessionResponse = {
                SessionId: userSession.id,
                LoginUser: userSession.login,
                UserId: userSession.userId,
                Status: userSession.status,
                CreatedAt: userSession.createdAt,
                ExpiresAt: userSession.expiresAt

              } as ServerModels.OktaUserSessionResponse;
              //cache the OKTA session
              this.oktaAuth.storeOktaSession(sessionResponse);
              // Trigger the notification
              this.oktaAuth.$userSession.next(sessionResponse);
            }
          },
          error: (err: HttpErrorResponse) => {
            // 404 no user session, that is ok
          }
        });
      } else {
        // Use the cached session and trigger the notification
        this.oktaAuth.$userSession.next(cachedOktaSession);
      }
    }
  }

  private loginSuccess(auth: ServerModels.LoginReponse) {
    // ExpiresAt is the token expiration time in seconds
    let tokenExpiresIn = Date.now() + auth.ExpiresAt * 1000
    sessionStorage.setItem(environment.authTokenStorageKey, auth.AccessToken);
    sessionStorage.setItem(environment.authTokenExpiresStorageKey, tokenExpiresIn.toString());
    if (auth.RefreshToken != null) {
      sessionStorage.setItem(environment.authRefreshTokenStorageKey, auth.RefreshToken);
      this.tokenService.startRefreshTokenTimer();
    }

    if (auth.Status == ResponseMessageEnum.RegistrationRequired) {
      this.router.navigate(['/register']);
    }

    this.idle.resetIdle();

    this.loginService.getUserPreferenceTypes().subscribe((types: ServerModels.ENESS_REF_UserPreferenceTypes[]) => this.loginService.nextUserPreferenceType(types));
    this.loginService.getUserInfo().pipe(takeUntil(this._unsubscribe$)).subscribe({
      next: (info: any) => {
        if (info == null) {
          this.router.navigate(['/register']);
        } else
          if (info && info.ApprovalStatus.toLowerCase() === 'pending approval') {
            this.displayAlert("Your NESS registration request is pending approval.  Once approved, you may log into NESS.");
            this.isAuthenticated = false;
          } else if (info && info.Deleted == true) { // Allow deleted user to re-register
            this.loginService.nextUserInfo(info);
            this.router.navigate(['/register']);
          } else if (info && (info.ApprovalStatus.toLowerCase() === 'denied')) {
            this.loginService.nextUserInfo(info);
            this.router.navigate(['/register']);
          } else {
            this.isAuthenticated = info.ApprovalStatus.toLowerCase() === 'approved' && !info.TwoFactorEnabled;
            if (this.isAuthenticated && auth.AccessToken) {
              this.loginService.nextUserInfo(info);
              Promise.resolve(this.infoReportGuard.hasIRAssignment()).then((val) => {
                if (val) {
                  sessionStorage.setItem(this.infoReportService.hasInfoReportStorageKey, JSON.stringify(true));
                }
                else {
                  sessionStorage.removeItem(this.infoReportService.hasInfoReportStorageKey);
                }
                let redirectUrl = sessionStorage.getItem('redirectUrl');
                if (redirectUrl != null) {
                  this.redirectUrl = redirectUrl;
                }
                this.redirect();
              });
            }
          }
      },
      error: (err: HttpErrorResponse) => {
        this.displayAlert(err.message);
        this.isLoginError = true;
      }
    });
  }

  private loginFailed(err: HttpErrorResponse) {
    if (err.status == 401) {
      //if (err.error == ResponseMessageEnum.MFACodeInvalid) {
      //  this.isAuthenticated = true;
      //  this.loginForm.form.controls.code.setValue(null);
      //  this.oktaStateToken = "";
      //  this.oktaFactorId = "";
      //  this.validateUserLogin(this.loginForm.form.controls.username.value, this.loginForm.form.controls.password.value);
      //}
      let accountType = err.error == ResponseMessageEnum.LDAPAccountLocked || err.error == ResponseMessageEnum.LDAPAccountDisabled || err.error == ResponseMessageEnum.LDAPLoginError ?
        { account: '', contact: 'the Help Desk' } : { account: 'NESS', contact: 'your NESS Application Administrator' };
      if (err.error != ResponseMessageEnum.NotAuthenticated && err.error != ResponseMessageEnum.RequireAuthCode) {
        this.alertStyle = 'alert alert-warning';
        this.alertIconStyle = 'fas fa-exclamation-circle';
        let errorMessage = err.error
        let tempMessage = err.error.split("|", 1);
        if (tempMessage) {
          errorMessage = tempMessage;
        }
        if (err.error == ResponseMessageEnum.LDAPAccountLocked || err.error == ResponseMessageEnum.OktaAccountLockedOut) {
          this.displayAlert(`You have exceeded the maximum number of login attempts. Your Okta account is now locked. Please contact NESS_Support@atf.gov.`);
        } else if (err.error == ResponseMessageEnum.AccountLocked) {
          this.displayAlert(`You have exceeded the maximum number of login attempts. Your Okta account is now locked. Please contact NESS Support.`);
        } else if (errorMessage == ResponseMessageEnum.AccountInactive || errorMessage == ResponseMessageEnum.AccountDisabled) {
          let adminJson = err.error.split("|", 2);
          if (adminJson) {
            this.adminsList = JSON.parse(adminJson.slice(1))
          }
          this.displayAlert("Your NESS account is currently disabled. Please contact a NESS Application Administrator for assistance with enabling your account:");
        } else if (err.error == ResponseMessageEnum.LDAPAccountDisabled) {
          this.displayAlert(`Your ${accountType.account} account is currently disabled. Please contact NESS_Support@atf.gov for assistance with enabling your account.`);
        } else if (err.error == ResponseMessageEnum.RequirePasswordReset || err.error == ResponseMessageEnum.PasswordExpired || err.error == ResponseMessageEnum.OktaPasswordExpired) {
          this.router.navigate(['/resetpw']);
        } else if (err.error == ResponseMessageEnum.LDAPLoginError) {
          this.displayAlert('Invalid username or password. Please check your username and password. Your account will be locked after three unsuccessful login attempts.');
        } else {
          this.displayAlert('Unable to authenticate your username and password at this time. Please contact NESS_Support@atf.gov for assistance.')
        }
        this.isLoginError = true;
      } else {
        this.alertStyle = 'alert alert-info';
        this.alertIconStyle = 'fas fa-exclamation-circle';
        this.displayAlert("Our records do not recognize you as an authenticated user to request access to NESS. Please contact NESS_Support@atf.gov for assistance. Once you are authenticated, you may continue with the new user registration process to access NESS.");
      }
    }

    if (err.status == 423) {
      let adminJson = err.error.split("|", 2);
      if (adminJson) {
        this.adminsList = JSON.parse(adminJson.slice(1))
      }
      this.displayAlert("Your NESS account is currently disabled. Please contact a NESS Application Administrator for assistance with enabling your account:");
    }
  }

  public validateUserToken() {
    let token: string = sessionStorage.getItem(environment.authTokenStorageKey);
    let tokenExpires: number = Number.parseInt(sessionStorage.getItem(environment.authTokenExpiresStorageKey));
    if (token && tokenExpires >= Date.now()) {
      this.loginService.getUserPreferenceTypes().subscribe((types: ServerModels.ENESS_REF_UserPreferenceTypes[]) => this.loginService.nextUserPreferenceType(types));
      this.loginService.getUserInfo().pipe(takeUntil(this._unsubscribe$)).subscribe({
        next: (info: any) => {
          let isApprovedEnabled: boolean = info && info.ApprovalStatus.toLowerCase() == 'approved' && !info.Disabled;
          if (isApprovedEnabled) {
            this.loginService.nextUserInfo(info);
            Promise.resolve(this.infoReportGuard.hasIRAssignment()).then((val) => {
              if (val) {
                sessionStorage.setItem(this.infoReportService.hasInfoReportStorageKey, JSON.stringify(true));
              }
              else {
                sessionStorage.removeItem(this.infoReportService.hasInfoReportStorageKey);
              }
              let redirectUrl = sessionStorage.getItem('redirectUrl');
              if (redirectUrl != null) {
                this.redirectUrl = redirectUrl;
              }
              this.redirect();
            });
          }

          if (info && info.ApprovalStatus.toLowerCase() === 'pending approval') {
            this.displayAlert("Your NESS registration request is pending approval.  Once approved, you may log into NESS.");
          }

          // Handle deleted user account
          if (info && info.Deleted == true) {
            this.router.navigate(['/register']);
          }

          if (info && info.ApprovalStatus.toLowerCase() !== 'approved' && info.ApprovalStatus.toLowerCase() !== 'pending approval') {
            this.router.navigate(['/register']);
          }

          if (!info) {
            this.router.navigate(['/register']);
          }
        },
        error: (err: any) => {
          console.log(err);
        }
      });
    }
  }

  public internalUserLogin(authCookie: string) {
    if (authCookie != null) {
      sessionStorage.removeItem(environment.authTokenStorageKey);
      sessionStorage.removeItem(environment.authTokenExpiresStorageKey);
      sessionStorage.removeItem(environment.authRefreshTokenStorageKey);

      let tokenExpire = Number.parseInt(this.cookieService.get("token_expire").toString());
      let expDate = new Date();
      // Set token Expires
      expDate.setMinutes(expDate.getMinutes() + tokenExpire);

      let refreshToken = this.cookieService.get(environment.authRefreshTokenCookieStorageKey);
      sessionStorage.setItem(environment.authRefreshTokenStorageKey, refreshToken);

      sessionStorage.setItem(environment.authTokenStorageKey, authCookie);
      sessionStorage.setItem(environment.authTokenExpiresStorageKey, Date.parse(expDate.toString()).toString());

      this.cookieService.remove(environment.authCookieStorageKey, { domain: environment.authCookieDomainKey });
      this.cookieService.remove(environment.authRefreshTokenCookieStorageKey, { domain: environment.authCookieDomainKey });
      this.cookieService.remove("token_expire", { domain: environment.authCookieDomainKey });

      this.tokenService.startRefreshTokenTimer();

    }
  }

  private displayAlert(message: string) {
    this.alertMessage = message;
    this.isLoginError = true;
  }

  private redirect() {
    if (this.redirectUrl != undefined && this.redirectUrl != null) {
      sessionStorage.removeItem('redirectUrl');
      this.router.navigateByUrl(this.redirectUrl);
    }
    else {
      this.router.navigate(['/home'])
    }
  }

}
